/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback } from 'react';
import type { KeyboardEventHandler } from 'react';

import { useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { jsx } from '@atlaskit/css';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { HyperlinkPlugin } from '@atlaskit/editor-plugins/hyperlink';
import LinkIcon from '@atlaskit/icon/core/link';

import { useFireAnalyticsEvent } from '../../hooks/useAnalyticsEvent';

import messages from './messages';

type Props = {
	editorApi?: PublicPluginAPI<[HyperlinkPlugin]>;
	promptType: 'user-input' | 'interrogate';
};

export const LinkPickerButton = ({ editorApi, promptType }: Props): JSX.Element => {
	const { formatMessage } = useIntl();

	const { activeLinkMark } = useSharedPluginStateWithSelector(
		editorApi,
		['hyperlink'],
		(states) => {
			return {
				activeLinkMark: states.hyperlinkState?.activeLinkMark,
			};
		},
	);

	const fireAnalyticsEvent = useFireAnalyticsEvent();

	const handleClick = useCallback(() => {
		if (editorApi) {
			const showLinkToolbarAction = editorApi.hyperlink?.commands.showLinkToolbar(
				INPUT_METHOD.QUICK_INSERT,
			);
			if (showLinkToolbarAction) {
				editorApi.core.actions.execute(showLinkToolbarAction);
			}
		}

		fireAnalyticsEvent({
			payload: {
				action: ACTION.CLICKED,
				actionSubject: ACTION_SUBJECT.BUTTON,
				actionSubjectId: ACTION_SUBJECT_ID.AI_PROMPT_LINK_PICKER_BUTTON,
				attributes: {
					promptType,
				},
				eventType: EVENT_TYPE.UI,
			},
		});
	}, [editorApi, fireAnalyticsEvent, promptType]);

	// The editor can be wrapped in a component that process keyboard events,
	// but we don't need to propagate `Enter`, `Space`, and `Tab` key event to the editor.
	const stopPropagationForActionKeys: KeyboardEventHandler = useCallback((event) => {
		if (['Enter', ' ', 'Tab'].includes(event.key)) {
			event.stopPropagation();
		}
	}, []);

	return (
		<IconButton
			isTooltipDisabled={false}
			label={formatMessage(messages.label)}
			testId="link-picker-button"
			icon={LinkIcon}
			isSelected={!!activeLinkMark}
			spacing="compact"
			appearance="subtle"
			onClick={handleClick}
			onKeyDown={stopPropagationForActionKeys}
			onKeyUp={stopPropagationForActionKeys}
		/>
	);
};
